// app/(app)/smartusers/index.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@components/Layout';
import USAMap from '@components/USAMap';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@utils/Date';
import { toast } from 'react-hot-toast';
import Modal from '@/components/Modal';
import {
  collection,
  doc,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
} from 'firebase/firestore';
import { db } from '@root/firebase';

import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';
import ConnectionRequestButton from '@components/ConnectionRequestButton';

/* ---------------- helpers ---------------- */
function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function isPublicProfile(userDoc) {
  return !(userDoc?.settings?.main?.profilePrivate === true);
}

/* ---------------- tiny card component ---------------- */
function UserCard({
  user,
  isFriend,
  isPending,
  globalData,
  navigate,
  onPending, // ✅ callback from parent to update pendingSet
}) {
  const { id, name, avatar, city, region, numDogs, joinedDate } = user;

  return (
    <div className='card contact-bx item-content'>
      <div className='card-body user-profile pb-0'>
        <div className='d-flex align-items-center mb-3'>
          <div className='image-bx'>
            <img
              src={avatar || '/default-avatar.jpg'}
              alt={name}
              className='rounded-circle'
            />
            <span className='active'></span>
          </div>
          <div className='ms-3 text-start'>
            <h4 className='fs-14 font-w600 text-truncate'>{name}</h4>
            <span className='fs-12 mb-2 d-block text-truncate'>
              {city || 'Unknown'}, {region || 'Unknown'}
            </span>
            <span className='fs-12 text-primary d-block'>
              Joined {joinedDate || '—'}
            </span>
          </div>
        </div>

        <div className='user-meta-info mb-3'>
          <div className='col d-flex justify-content-center'>
            {/* Dogs */}
            <div className='tooltip-container'>
              <button
                type='button'
                className='reaction-btn reaction-action'
                title='View Tribute'
              >
                <i className='fas fa-paw reaction-icon' />
              </button>
              <span className='tooltip'>Dogs {numDogs ?? 0}</span>
            </div>

            {/* Like */}
            <div className='col d-flex justify-content-center'>
              <LikeButton
                collectionPath={`users`}
                documentId={id}
                userId={globalData.uid}
                styled={true}
              />
            </div>

            {/* Favorite */}
            <div className='col d-flex justify-content-center'>
              <FavoriteButton
                collectionPath={`users`}
                documentId={id}
                userId={globalData.uid}
                styled={true}
              />
            </div>

            {/* View Profile (explicit button) */}
            <div className='col d-flex justify-content-center'>
              <div className='tooltip-container'>
                <button
                  type='button'
                  className='reaction-btn reaction-action'
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/app/profile/${id}`);
                  }}
                >
                  <i className='fas fa-search reaction-icon' />
                </button>
                <span className='tooltip'>{`View ${name}`}</span>
              </div>
            </div>

            {/* ✅ Reusable Connection Request */}
            <ConnectionRequestButton
              senderId={globalData.uid}
              senderName={globalData?.name}
              senderAvatar={globalData?.avatar}
              receiverId={id}
              isFriend={isFriend}
              isPending={isPending}
              onPending={(uid) => onPending?.(uid)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- page ---------------- */
export default function SmartUsersPage() {
  const { globalUser, globalData, defaultAvatarUrl } = useAuth();
  const { type } = useParams();
  const navigate = useNavigate();

  const latestFetchIdRef = useRef(0);

  const accountType = type === 'participants' ? 'Participant' : 'User';
  const [users, setUsers] = useState([]);
  const [friendsSet, setFriendsSet] = useState(new Set());
  const [favsSet, setFavsSet] = useState(new Set());
  const [pendingSet, setPendingSet] = useState(new Set());

  const [totalUsers, setTotalUsers] = useState(0);
  const [participantCount, setParticipantCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [sortBy, setSortBy] = useState('joined_desc'); // joined_desc | name_asc | dogs_desc

  const [isLoading, setIsLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const pageSize = 12;

  /* counts */
  useEffect(() => {
    (async () => {
      try {
        const usersRef = collection(db, 'users');
        const uSnap = await getDocs(
          query(usersRef, where('accountType', '==', 'User'))
        );
        const pSnap = await getDocs(
          query(usersRef, where('accountType', '==', 'Participant'))
        );
        setTotalUsers(uSnap.size);
        setParticipantCount(pSnap.size);
      } catch {}
    })();
  }, []);

  /* favourites for sidebar */
  useEffect(() => {
    (async () => {
      if (!globalUser?.uid) return;
      try {
        const favRef = collection(db, 'users', globalUser.uid, 'favorites');
        const favSnap = await getDocs(favRef);
        const favorites = favSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setFavsSet(new Set(favorites.map((f) => f.id)));
      } catch {}
    })();
  }, [globalUser?.uid]);

  /* friends + pending */
  useEffect(() => {
    (async () => {
      if (!globalUser?.uid) return;
      try {
        const connSnap = await getDocs(
          collection(db, 'users', globalUser.uid, 'connections')
        );
        setFriendsSet(new Set(connSnap.docs.map((d) => d.id)));
      } catch {}
      try {
        const notifSnap = await getDocs(
          collection(db, 'users', globalUser.uid, 'notifications')
        );
        const pendingFrom = notifSnap.docs
          .filter((d) => d.data()?.status === 'pending')
          .map((d) => d.id);
        setPendingSet(new Set(pendingFrom));
      } catch {}
    })();
  }, [globalUser?.uid]);

  /* search + list users */
  useEffect(() => {
    fetchUsers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType, debouncedSearch]);

  async function fetchUsers(reset = false) {
    const fetchId = ++latestFetchIdRef.current;
    if (reset) setLastDoc(null);
    setIsLoading(true);
    try {
      const usersRef = collection(db, 'users');
      let qRef;

      const term = debouncedSearch.trim().toLowerCase();

      if (term) {
        qRef = query(
          usersRef,
          where('accountType', '==', accountType),
          orderBy('searchName'),
          startAt(term),
          endAt(term + '\uf8ff'),
          limit(pageSize)
        );
      } else {
        qRef = query(
          usersRef,
          where('accountType', '==', accountType),
          orderBy('joinedDate', 'desc'),
          limit(pageSize)
        );
      }

      const snap = await getDocs(qRef);

      const rows = await Promise.all(
        snap.docs.map(async (d) => {
          const u = { id: d.id, ...d.data() };
          if (u.id === globalUser?.uid) return null;
          if (!isPublicProfile(u)) return null;

          // Address (best effort)
          let city = 'Unknown';
          let region = 'Unknown';
          try {
            const addrColl = collection(db, `users/${u.id}/address`);
            const addrSnap = await getDocs(addrColl);
            if (!addrSnap.empty) {
              const first = addrSnap.docs[0].data();
              city = first.city || first.town || 'Unknown';
              region = first.region || first.state || 'Unknown';
            }
          } catch {}

          // Dogs count
          let numDogs = 0;
          try {
            const cnt = await getCountFromServer(
              collection(db, `users/${u.id}/dogs`)
            );
            numDogs = cnt.data().count || 0;
          } catch {}

          return {
            id: u.id,
            name: u.name || 'Unknown',
            searchName: (u.name || '').toLowerCase(),
            email: u.email || 'Unknown',
            avatar: u.avatar || defaultAvatarUrl,
            backgroundImage: u.backgroundImage,
            city,
            region,
            numDogs,
            joinedDate: u.joinedDate ? formatDate(u.joinedDate) : '—',
            accountType,
          };
        })
      );

      if (fetchId !== latestFetchIdRef.current) return;

      const clean = rows.filter(Boolean);

      // client sort (name/dogs)
      const sorted = [...clean].sort((a, b) => {
        if (sortBy === 'name_asc')
          return a.searchName.localeCompare(b.searchName);
        if (sortBy === 'dogs_desc') return (b.numDogs || 0) - (a.numDogs || 0);
        return 0; // joined_desc already set by Firestore
      });

      setUsers(reset ? sorted : (prev) => [...prev, ...sorted]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
    } catch (e) {
      console.error('Error fetching users:', e);
      toast.error('Failed to fetch users');
    } finally {
      if (fetchId === latestFetchIdRef.current) setIsLoading(false);
    }
  }

  async function fetchNextPage() {
    if (!lastDoc) return;
    const fetchId = ++latestFetchIdRef.current;
    setIsLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const qRef = query(
        usersRef,
        where('accountType', '==', accountType),
        orderBy('joinedDate', 'desc'),
        startAfter(lastDoc),
        limit(pageSize)
      );
      const snap = await getDocs(qRef);

      const rows = await Promise.all(
        snap.docs.map(async (d) => {
          const u = { id: d.id, ...d.data() };
          if (u.id === globalUser?.uid) return null;
          if (!isPublicProfile(u)) return null;

          // Address
          let city = 'Unknown';
          let region = 'Unknown';
          try {
            const addrSnap = await getDocs(
              collection(db, `users/${u.id}/address`)
            );
            if (!addrSnap.empty) {
              const first = addrSnap.docs[0].data();
              city = first.city || first.town || 'Unknown';
              region = first.region || first.state || 'Unknown';
            }
          } catch {}

          // Dogs
          let numDogs = 0;
          try {
            const cnt = await getCountFromServer(
              collection(db, `users/${u.id}/dogs`)
            );
            numDogs = cnt.data().count || 0;
          } catch {}

          return {
            id: u.id,
            name: u.name || 'Unknown',
            searchName: (u.name || '').toLowerCase(),
            email: u.email || 'Unknown',
            avatar: u.avatar || defaultAvatarUrl,
            backgroundImage: u.backgroundImage,
            city,
            region,
            numDogs,
            joinedDate: u.joinedDate ? formatDate(u.joinedDate) : '—',
            accountType,
          };
        })
      );

      if (fetchId !== latestFetchIdRef.current) return;

      const clean = rows.filter(Boolean);
      setUsers((prev) => [...prev, ...clean]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
    } catch (e) {
      console.error('Error fetching next page:', e);
    } finally {
      if (fetchId === latestFetchIdRef.current) setIsLoading(false);
    }
  }

  // when a request becomes pending, reflect locally
  function markPending(uid) {
    setPendingSet((s) => {
      const n = new Set(s);
      n.add(uid);
      return n;
    });
  }

  /* derived */
  const filteredUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      if (sortBy === 'name_asc')
        return a.searchName.localeCompare(b.searchName);
      if (sortBy === 'dogs_desc') return (b.numDogs || 0) - (a.numDogs || 0);
      return 0;
    });
    return sorted;
  }, [users, sortBy]);

  return (
    <Layout>
      <div className='row'>
        {/* Search Bar */}
        <div className='col-xl-12'>
          <div className='card p-3'>
            <div className='input-group'>
              <input
                type='text'
                id='txtSearch'
                className='form-control'
                placeholder='Search users'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                className='btn btn-primary'
                onClick={() => fetchUsers(true)}
                disabled={isLoading}
              >
                {isLoading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>
        </div>

        {/* Map (kept as-is) */}
        <div className='col-xl-12'>
          <div className='card'>
            <div className='card-body'>
              <div className='profile-statistics'>
                <div className='text-center'>
                  <USAMap
                    width={980}
                    height={200}
                    scale={520}
                    showTooltip
                    tooltipNearCursor
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Profiles */}
        {isLoading && users.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className='col-xl-4 col-xxl-4 col-lg-4 col-md-6 col-sm-6 items'
            >
              <div
                className='card contact-bx item-content'
                style={{ height: 210 }}
              />
            </div>
          ))
        ) : filteredUsers.length > 0 ? (
          <>
            {filteredUsers.map((u) => {
              const isFriend = friendsSet.has(u.id);
              const isPending = pendingSet.has(u.id);

              return (
                <div
                  key={u.id}
                  className='col-xl-3 col-xxl-4 col-lg-4 col-md-6 col-sm-6 items'
                >
                  <UserCard
                    user={u}
                    isFriend={isFriend}
                    isPending={isPending}
                    globalData={globalData}
                    navigate={navigate}
                    onPending={markPending}
                  />
                </div>
              );
            })}

            {/* Load more */}
            {lastDoc && (
              <div className='col-12'>
                <div className='d-flex justify-content-center my-3'>
                  <button
                    className='btn btn-outline-primary'
                    onClick={fetchNextPage}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className='col-12'>
            <div className='text-center text-muted py-5'>
              <i className='mdi mdi-account-off-outline display-6 d-block mb-2' />
              No public {accountType.toLowerCase()}s found.
            </div>
          </div>
        )}
      </div>
      {/* Removed old page-level confirm modal; handled inside the reusable component */}
    </Layout>
  );
}
